import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { FileText, MessageCircle } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export const Hero = () => {
  const settings = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  const heroBg =
    "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center text-center overflow-hidden"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <img
          src={heroBg}
          alt="Luxe verbouwing"
          className="w-full h-[120%] object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/65 z-10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60 z-10" />

      <div className="relative z-20 flex flex-col items-center gap-7 max-w-4xl mx-auto px-6">
        <motion.img
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          src={resolveImg(settings.logo_url)}
          alt="Klusbedrijf Pruijn"
          style={{ height: `${settings.hero_logo_height}px` }}
          className="w-auto object-contain max-w-[80vw]"
          data-testid="hero-logo"
        />
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
          className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]"
        >
          {settings.hero_title_line1} <br className="hidden sm:block" />
          <span className="text-[#FF1A1A]">{settings.hero_title_line2}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
          className="text-base md:text-lg text-white/70 font-light max-w-2xl leading-relaxed"
        >
          {settings.hero_subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center gap-4 mt-3"
        >
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-quote-button"
            className="bg-[#FF1A1A] text-white px-8 py-4 font-semibold hover:bg-[#E60000] transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <FileText size={18} strokeWidth={2} /> Gratis offerte
          </button>
          <a
            href={`https://wa.me/${settings.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="bg-white text-black px-8 py-4 font-semibold hover:bg-gray-200 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <MessageCircle size={18} strokeWidth={2} /> WhatsApp
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/40 text-xs tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-[#FF1A1A] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
